import React, { useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCloudArrowUp, faCloudArrowDown, faImage } from '@fortawesome/free-solid-svg-icons';

const RemoveBackground = () => {
  const [originalImage, setOriginalImage] = useState(null);
  const [processedImage, setProcessedImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setOriginalImage(e.target.result);
        setProcessedImage(null);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeBackground = async () => {
    if (!originalImage) return;
    setLoading(true);

    try {
      // Create a canvas element
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');

      // Load the image
      const img = new Image();
      img.src = originalImage;
      await new Promise((resolve) => {
        img.onload = resolve;
      });

      // Set canvas dimensions to match image
      canvas.width = img.width;
      canvas.height = img.height;

      // Draw the original image
      ctx.drawImage(img, 0, 0);

      // Get image data
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;

      // Simple background removal algorithm (using green screen approach)
      // You can modify these threshold values based on your needs
      const threshold = 150;
      
      for (let i = 0; i < data.length; i += 4) {
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];
        
        // Check if pixel is close to white or light colored
        if (r > threshold && g > threshold && b > threshold) {
          data[i + 3] = 0; // Set alpha to 0 (transparent)
        }
      }

      // Put the processed image data back
      ctx.putImageData(imageData, 0, 0);

      // Convert canvas to data URL
      const processedDataUrl = canvas.toDataURL('image/png');
      setProcessedImage(processedDataUrl);
    } catch (error) {
      console.error('Error processing image:', error);
    } finally {
      setLoading(false);
    }
  };

  const downloadImage = () => {
    if (processedImage) {
      const link = document.createElement('a');
      link.download = 'processed-image.png';
      link.href = processedImage;
      link.click();
    }
  };

  return (
    <div class="grid grid-cols-1 xl:grid-cols-2 xl:gap-4">
      <div className="col-span-2 xl:col-auto">
        <div className="p-4 h-full space-y-6 bg-white border border-gray-200 rounded-lg shadow-sm 2xl:col-span-2 dark:border-gray-700 sm:p-6 dark:bg-gray-800">
          <div className="px-4 py-2 font-medium text-md border border-gray-200 border-dashed rounded dark:border-gray-600">
            <h3 className='uppercase border-l-4 border-blue-600 p-2'>Original Image</h3>
          </div>
          <div className="px-4 py-2 font-medium text-md rounded dark:border-gray-600">
            <input
              type="file"
              ref={fileInputRef}
              accept="image/*"
              className="hidden"
              onChange={handleImageUpload}
            />
            <button
              className="items-center gap-2 px-5 py-2.5 bg-blue-500 text-white font-medium rounded-lg hover:bg-blue-600"
              onClick={() => fileInputRef.current.click()}
            >
              <FontAwesomeIcon
                style={{ width: '18px' }}
                icon={faCloudArrowUp}
              />
              {` `}&nbsp; Upload Image
            </button>
            <button
              onClick={removeBackground}
              disabled={!originalImage || loading}
              className={`ml-4 items-center gap-2 px-5 py-2.5 bg-green-500 text-white font-medium rounded-lg hover:bg-green-600 ${
                (!originalImage || loading) && 'opacity-50 cursor-not-allowed'
              }`}
            >
              <FontAwesomeIcon
                style={{ width: '18px' }}
                icon={faImage}
              />
              {` `}&nbsp; Remove Background
            </button>
            {originalImage && (
              <div className="border mt-4 rounded p-4">
                <img
                  src={originalImage}
                  alt="Original"
                  className="max-w-full h-auto"
                />
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="col-span-2 xl:col-auto">
        <div className="p-4 h-full space-y-6 bg-white border border-gray-200 rounded-lg shadow-sm 2xl:col-span-2 dark:border-gray-700 sm:p-6 dark:bg-gray-800">
          <div className="px-4 py-2 font-medium text-md border border-gray-200 border-dashed rounded dark:border-gray-600">
            <h3 className='uppercase border-l-4 border-yellow-600 p-2'>Processed Image</h3>
          </div>
          <div className="px-4 py-2 font-medium text-md rounded dark:border-gray-600">
            {processedImage && (
              <button
                onClick={downloadImage}
                className="flex items-center gap-2 px-5 py-2.5 bg-purple-500 text-white font-medium rounded-lg hover:bg-purple-600"
              >
                <FontAwesomeIcon
                style={{ width: '18px' }}
                icon={faCloudArrowDown}
              />
              {` `}&nbsp; Download
              </button>
            )}
            {processedImage && (
              <div className="border mt-4 rounded p-4">
                <img
                  src={processedImage}
                  alt="Processed"
                  className="max-w-full h-auto"
                />
              </div>
            )}
          </div>
          <canvas ref={canvasRef} className="hidden" />
        </div>
      </div>
    </div>
  )
}

export default RemoveBackground;
